import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectPlantStatus,
  setSelectPlantStatus,
} from "../../../redux/features/sites/sites-slice";
import IListAllSolarPlantsRequestDTO from "../../../shared/oversight-core/dtos/request-dtos/list-all-solar-plants-response-dto";
import { EPlantStatus } from "../../../shared/oversight-core/enums/plant-status";
import { ESolarPlantSize } from "../../../shared/oversight-core/enums/solar-plant-size";
import FilteringSidePanel from "../../../shared/oversight-core/shared-components/filtering-side-panel/filtering-side-panel";
import AppSelect, {
  Option,
} from "../../../shared/oversight-core/ui-elements/app-select/app-select";

interface IProps {
  show: boolean;
  onClose: () => void;
  updateFilters: (
    generationServiceProviderId: string,
    solarPlantSize?: ESolarPlantSize,
    plantStatus?: EPlantStatus
  ) => void;
  setFilterBadgeValue: React.Dispatch<React.SetStateAction<number>>;
  filters: IListAllSolarPlantsRequestDTO;
  solarBrandList: Option[];
}

const solarBrandDefaultOption: Option = {
  value: "",
  label: "Select a solar brand",
};

const solarSizeDefaultOption: Option = {
  value: "",
  label: "Select a size",
};

const solarPlatStatusDefaultOption: Option = {
  value: "",
  label: "All Sites",
};

const sizeOption: Option[] = [
  { value: ESolarPlantSize.SMALL, label: "0 - 10KW" },
  { value: ESolarPlantSize.MEDIUM, label: "10kW - 50kW" },
  { value: ESolarPlantSize.LARGE, label: "50kW - 100kW" },
  { value: ESolarPlantSize.EXTRA_LARGE, label: "Greater than 100kW" },
];

const plantStatus: Option[] = [
  { value: EPlantStatus.GENERATING, label: "Active Sites" },
  { value: EPlantStatus.OFFLINE, label: "Offline Sites" },
  { value: EPlantStatus.FAULT, label: "Error Sites" },
  { value: EPlantStatus.DISABLED, label: "Disabled" },
];

const SitesFilteringSidePanel = (props: IProps) => {
  const {
    show,
    filters,
    onClose,
    updateFilters,
    setFilterBadgeValue,
    solarBrandList,
  } = props;

  const [selectedSolarBrand, setSelectedSolarBrand] = useState<Option>({
    ...solarBrandDefaultOption,
  });
  const [selectedSize, setSelectedSize] = useState<Option>({
    ...solarSizeDefaultOption,
  });

  const [selectedStatus, setSelectedStatus] = useState<Option>({
    ...solarPlatStatusDefaultOption,
  });

  const dispatch = useDispatch();

  const selectStatus = useSelector(selectPlantStatus);

  useEffect(() => {
    if (!filters.generationServiceProviderId) {
      setSelectedSolarBrand({ ...solarBrandDefaultOption });
    }

    if (!filters.solarPlantSize) {
      setSelectedSize({ ...solarSizeDefaultOption });
    }

    if (!filters.plantStatus || selectStatus) {
      setSelectedStatus({ ...solarPlatStatusDefaultOption });
    }
  }, [filters, selectStatus]);

  useEffect(() => {
    if (
      selectedStatus?.value &&
      selectedSize.value &&
      selectedSolarBrand?.value
    ) {
      setFilterBadgeValue(3);
    } else if (
      (selectedStatus?.value && selectedSize.value) ||
      (selectedStatus?.value && selectedSolarBrand?.value) ||
      (selectedSize.value && selectedSolarBrand?.value)
    ) {
      setFilterBadgeValue(2);
    } else if (
      selectedStatus?.value ||
      selectedSize.value ||
      selectedSolarBrand?.value
    ) {
      setFilterBadgeValue(1);
    } else {
      setFilterBadgeValue(0);
    }
  }, [
    selectedStatus?.value,
    selectedSize.value,
    selectedSolarBrand?.value,
    setFilterBadgeValue,
  ]);

  return (
    <FilteringSidePanel
      isOpen={show}
      onClose={onClose}
      onFilter={() => {
        updateFilters(
          selectedSolarBrand.value,
          selectedSize.value as ESolarPlantSize,
          selectedStatus.value as EPlantStatus
        );
        onClose();
      }}
      onClearAll={() => {
        onClose();
        setSelectedSolarBrand({
          ...solarBrandDefaultOption,
        });
        setSelectedSize({ ...solarSizeDefaultOption });
        updateFilters("", undefined, undefined);
        dispatch(setSelectPlantStatus(undefined));
      }}
    >
      <>
        <AppSelect
          className="text-light font-size-14"
          selectedValue={selectedSolarBrand}
          label="Solar Brand"
          options={solarBrandList}
          onChangeOption={(selectedOption) => {
            setSelectedSolarBrand(selectedOption);
          }}
          isRequired={false}
          labelFontSize="font-size-14"
        />
        <AppSelect
          className="text-light font-size-14 mt-4"
          selectedValue={selectedSize}
          label="Size"
          options={sizeOption}
          onChangeOption={(selectedOption) => {
            setSelectedSize(selectedOption);
          }}
          errors={Error}
          isRequired={false}
          labelFontSize="font-size-14"
        />
        <AppSelect
          className="text-light font-size-14 mt-4"
          selectedValue={selectedStatus}
          label="Status"
          options={plantStatus}
          onChangeOption={(selectedOption) => {
            setSelectedStatus(selectedOption);
          }}
          errors={Error}
          isRequired={false}
          labelFontSize="font-size-14"
        />
      </>
    </FilteringSidePanel>
  );
};

export default SitesFilteringSidePanel;
